/** The pure model of the Backlinks tab (1.J deliverable 15). */
import type { BacklinksV1, DocRefV1, LinkTypeV1, LinkV1 } from '@codex/contracts';

export type Relation = BacklinksV1['relations'][number];
export type Mention = BacklinksV1['mentions'][number];

export interface RelationGroup {
  readonly label: string;
  readonly items: readonly Relation[];
}

/**
 * The heading of a relation seen from `docId`: the type's inverse label for a link into the page
 * (its label when it has none); a symmetric type reads the same either way.
 */
export function relationLabel(
  link: Pick<LinkV1, 'targetId'>,
  type: Pick<LinkTypeV1, 'label' | 'inverseLabel' | 'symmetric'> | undefined,
  docId: string,
): string {
  if (type === undefined) return 'Related';
  if (type.symmetric) return type.label;
  return link.targetId === docId ? (type.inverseLabel ?? type.label) : type.label;
}

/** The relations grouped under their label, in the order each label first appears. */
export function groupRelations(
  relations: readonly Relation[],
  types: readonly LinkTypeV1[],
  docId: string,
): RelationGroup[] {
  const byId = new Map(types.map((t) => [t.id, t] as const));
  const groups = new Map<string, Relation[]>();
  for (const relation of relations) {
    const label = relationLabel(relation.link, byId.get(relation.link.linkTypeId), docId);
    const list = groups.get(label) ?? [];
    list.push(relation);
    groups.set(label, list);
  }
  return [...groups].map(([label, items]) => ({ label, items }));
}

/** True when a mention sits in a GM-only part of its page. */
export function isGmOnly(mention: Pick<Mention, 'inSecret' | 'inGmNote'>): boolean {
  return mention.inSecret || mention.inGmNote;
}

/** "Used in" and "Variants" show only when they hold something (a reference page). */
export function referenceGroups(data: Pick<BacklinksV1, 'usedIn' | 'variants'>): {
  readonly label: string;
  readonly pages: readonly DocRefV1[];
}[] {
  const groups = [
    { label: 'Used in', pages: data.usedIn },
    { label: 'Variants', pages: data.variants },
  ];
  return groups.filter((g) => g.pages.length > 0);
}
